<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>

        <transition name="fade-fast" mode="out-in">

            <div
                v-if="initialLoading || !totalRows"
                key="loadingOrEmpty"
                class="bg-gray-200 rounded-lg border d-flex justify-content-center align-items-center"
                style="min-height: 100px;"
            >
                <transition name="fade-fast" mode="out-in">
                    <div v-if="initialLoading || loading" key="loading" class="text-center">
                        <span class="text-black-50 text-uppercase font-weight-500 font-size-80">
                            Loading your tasks...
                        </span>
                        <!-- <busy-spinner inner-class="my-5" text="Loading your tasks..."></busy-spinner> -->
                    </div>
                    <div 
                        v-else
                        key="empty"
                        class="text-center"
                    >
                        <span class="font-size-200 opacity-50">
                            {{ getCongratsEmoji() }}
                        </span>
                        <br>
                        <span class="text-black-50 text-uppercase font-weight-500 font-size-80">
                            You Have No Open Workflow Tasks
                        </span>
                        
                    </div>
                </transition>
            </div>

            <b-card 
                v-else
                key="notEmptyList"
                id="active-tasks-table-card"
                class="shadow rounded-lg animate__animated animate__fadeIn animate__fast"
                no-body
            >

                <b-table 
                    id="tu-active-tasks-list"
                    :items="items"
                    :fields="visibleFields"
                    :busy="loading"
                    primary-key="id" 
                    responsive
                    no-local-sorting
                    no-sort-reset
                    :sort-by.sync="sortByField"
                    :sort-desc.sync="sortDesc"
                    @sort-changed="refreshList"
                    hover
                    tbody-class="cells-align-middle"
                    class="no-top-border td-align-middle mb-0"
                    thead-class="overflow-hidden cells-align-middle"
                    head-variant=""
                >
                    <template #cell(title)="data">
                        <b-link :href="data.item.urls.generic_redirect_url" target="_blank">{{ data.value }}</b-link>
                    </template>
                    <template #cell(deadline)="data">
                        <date-time-deadline
                            :date="data.value" 
                            empty-text="Not Set"
                            date-class="d-none"
                        ></date-time-deadline>
                    </template>
                </b-table>

                <template #footer v-if="totalRows">
                    <base-table-pagination
                        :current-page.sync="currentPage"
                        :per-page.sync="perPage"
                        :total-items="totalRows"
                        :show-per-page="showPerPage"
                        item-name="Tasks"
                        @update:perPage="refreshList"
                        @update:currentPage="getList"
                    ></base-table-pagination>
                </template>
            </b-card>

        </transition>        
        <!-- <pre>{{ items }}</pre> -->
    </div>
</template>

<script>
//import { errorHandling } from '../../js/custom-vue-mixins';
import { errorHandling, currentUserInfoMixin, splTableDefaults } from '@/js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import BaseTablePagination from '../BaseTablePagination.vue';
import DateTimeDeadline from '../table-cell-comps/DateTimeDeadline.vue';
import random from 'lodash/random';
//import { FormatDateHelper, FormatDateTimeLocal, HumanizeDateTimeHelper, DateApproachingDeadline } from '../../js/filters-helpers';

export default {
    name: 'ActiveTasksList',
    components: {
        BaseTablePagination,
        DateTimeDeadline,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        initialSortField: {  // initial field for sortBy, does not have to be in showFields array, but must be in fields array and must be in the ordering options for API endpoint
            type: String,
            default: 'deadline',
        },
        initialSortDesc: {  // initial sort in descending order?
            type: Boolean,
            default: false,
        },
        fixedPerPage: {  // if you want to force a fixed # per page AND HIDE PER-PAGE SELECTOR
            // this is useful for dashboard views where you want to control size of grid
            // validator... we use same one in base-table-pagination component, so using here too
            type: [String, Number],
            required: false,
            validator: function (value) {
                return splTableDefaults.perPageOptions.includes(value);
                //return defaultPerPageOptions.includes(value);
            },
        },
    },
    data() {
        return {
            testEmpty: true,
            errorMsg: 'Error',
            error: false,
            initialLoading: true,  // set to false after getting list the first time
            loading: false,
            items: [],  // the list from API
            totalRows: null,
            currentPage: 1,  // will get sync'd by base-table-pagination
            perPage: 25,  // will get sync'd by base-table-pagination, and also set by our mixin default value during create hook
            showPerPage: true,  // if we set fixedPerPage prop, this is set false in created() - and passed to same prop in pagination component
            // for sorting:
            sortByField: 'deadline',  // default gets set via prop... sortByField and sortDesc will get .sync'd by b-table, then gets used in API call query params
            sortDesc: false,  // sort descending = true (adds '-' sign in backend), sort ascending = false (no minus sign)
            fields: [
                // for column widths, do not set fixed or responsive, then use class: on field def object with either min-width or max-width (but not width directly)
                {
                    key: 'title',
                    label: 'Task',
                    sortable: false,
                    api_ordering_name: 'title',  // set this for any sortable field, this is what gets sent to API for ordering=_____
                    tdClass: 'font-weight-bold'
                },{
                    //key: 'workflow.workflow_spec_name',  // using dotted paths in these field defs causes issues...
                    key: 'workflow__workflow_spec__name',
                    label: 'Workflow Type',
                    sortable: false,
                    api_ordering_name: 'workflow__workflow_spec__name',
                    formatter: (value, key, item) => {
                        return item.workflow.workflow_spec_name;
                    },
                    sortByFormatted: true,
                    filterByFormatted: true,  // not using this yet, but if we do, we should set this to true
                    class: '',  // max-width-80
                },{
                    key: 'workflow_run_title',
                    label: 'Workflow',
                    sortable: false,
                    api_ordering_name: 'workflow__title',
                    class: '',  // max-width-80
                },{
                    //key: 'workflow.vendor.name',  // using dotted paths in these field defs causes issues...
                    key: 'workflow__vendor__name',
                    label: 'Vendor',
                    sortable: false,
                    api_ordering_name: 'workflow__vendor__name',
                    formatter: (value, key, item) => {
                        return item.workflow.vendor.name;
                    },
                    sortByFormatted: true,
                    filterByFormatted: true,  // not using this yet, but if we do, we should set this to true
                    class: '',  // max-width-80
                },{
                    key: 'deadline',
                    label: 'Task Deadline',
                    sortable: true,
                    api_ordering_name: 'deadline',
                    class: 'text-center',  // max-width-120
                },
            ],
            congratsEmojis: ['👏', '👍', '🥳', '🎉', '🚀', '🙌', '🎊']
        }
    },
    computed: {
        skeletonRows() {
            // the number of rows to show when loading
            // you might have perPage of 25, but likely only will show ~10... so make it closer to that
            // if you have fixedPerPage -- use that 
            return this.perPage;
        },
        isInternal() {
            return this.$currentUserInfo.is_internal;
        },
        isExternal() {
            return !this.$currentUserInfo.is_internal;
        },
        visibleFields() {
            const hideFieldIf = {
                'workflow__vendor__name': this.isExternal,
            };
            console.log('currentUserInfo:', this.$currentUserInfo);
            console.log('hideFieldIf:', hideFieldIf);
            return this.fields.filter(fld => !hideFieldIf[fld.key]);
            // TODO - might need to change other things to make sure we're not ordering by a field that got removed, for example
        },
    },
    created() {
        // merge any presetFilters (object from prop) with this.defaultFilterValues to make this.initialFilterValues
        /* if (this.presetFilters && !isEmpty(this.presetFilters)) {
            // target is empty object so you don't mutate originals, merge presetFilters last
            this.initialFilterValues = Object.assign({}, this.defaultFilterValues, this.presetFilters);  
        } else {
            // if we DO NOT have any presetFilters, we need to just do same thing, but without presetFilters... maybe there's a shorter way, but this works
            this.initialFilterValues = Object.assign({}, this.defaultFilterValues);
        }
        this.resetFilterValues(); */
        // FIGURE OUT THE perPage to use
        if (this.fixedPerPage) {
            this.perPage = Number(this.fixedPerPage);
            this.showPerPage = false;
        } else {
            this.perPage = splTableDefaults.perPage;  // splTableDefaults is from import
            this.showPerPage = true;
        }
        this.sortByField = this.initialSortField;  // set initial sort field based on prop
        this.sortDesc = this.initialSortDesc;  // set initial sort direction based on prop
    },
    mounted() {
        this.getList();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        refreshList() {
            // MAY BE CALLED BY PARENT USING $refs
            this.currentPage = 1;
            this.$nextTick(() => this.getList());
        },
        async getList() {
            this.loading = true;

            // BUILD QUERY PARAMS
            // axiosConfig['params'] is where we put search/filter params
            let filterParams = {  // grab the current search/filter values
                // search: this.currentFilterValues.searchFilter || null,
                // grouping: this.currentFilterValues.grouping, 
                // rfx_type: this.currentFilterValues.rfxTypeFilter || null,
                // sm_state: this.currentFilterValues.stateFilter || null,
                // sourcing_category: this.currentFilterValues.categoryFilter,
            };
            let orderingParams = {
                ordering: this.getOrderingFieldString(),
            };
            let paginationParams = {
                page: this.currentPage,
                page_size: this.perPage,
            };
            let combinedParams = Object.assign({}, filterParams, orderingParams, paginationParams);
            let axiosConfig = {params: combinedParams};

            let response;
            try {
                response = await suppliosApi.dashboard.tu_active_tasks(axiosConfig);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.items = response.data.results;  // paginated!
            this.totalRows = response.data.count;
            this.loading = false;
            this.initialLoading = false;
        },
        getOrderingFieldString: function() {
            // look up what field name (string) we should use to send to API, in case key here is different than what API should order by
            // useful if you have serializer methods that get values from related objects... you can use double-underscore notation in DRF for ordering
            // also add '-' if sortDesc is true
            let apiOrderingName = this.fields.find(fld => fld.key === this.sortByField).api_ordering_name;
            return (this.sortDesc ? '-' : '') + apiOrderingName;
        },
        getCongratsEmoji() {
            if (!this.congratsEmojis.length) {
                return '';
            }
            const upper = this.congratsEmojis.length - 1;
            const randomIndex = random(upper);  // returns random int between 0 and upper, INCLUSIVE
            return this.congratsEmojis[randomIndex];
        },
    }
}
</script>

<style lang="scss" scoped>

table.b-table[aria-busy='true'] {
    opacity: 0.5;
}

</style>